/**
 * Murojaat holatini kuzatish — ID Tracking (ЧТЗ 4.2.11.4).
 *
 *   * 1-usul: murojaat ID + telefonning oxirgi 4 raqami;
 *   * 2-usul: murojaat ID + SMS-OTP (6 xonali kod, 5 daqiqa amal qiladi).
 *
 * Muvaffaqiyatsiz urinishlar backendda cheklanadi (rate-limit, CAPTCHA) va
 * jurnalga yoziladi. Kartochkada ijrochi faqat umumiy nom bilan chiqadi —
 * inspektorning F.I.Sh. va telefoni ko'rsatilmaydi.
 */
import { useEffect, useRef, useState } from "react";
import type { CSSProperties, FormEvent } from "react";

import { ApiError } from "@/shared/api/client";
import { appealsApi } from "@/shared/api/endpoints";
import type { AppealTrack } from "@/shared/api/types";
import {
  APPEAL_ID_PATTERN,
  APPEAL_ID_SAMPLE,
  appealStatusLabels,
  trackingSteps,
} from "@/shared/data/content";
import { useAppealTypes } from "@/shared/data/portal";
import { useI18n } from "@/shared/i18n";
import { formatDate, formatDateTime } from "@/shared/lib/format";
import { Icon } from "@/shared/ui/Icon";

import { SplitHead } from "./SplitHead";

type Method = "last4" | "otp";

interface TrackState {
  phase: "idle" | "loading" | "done" | "error";
  data?: AppealTrack;
  message?: string;
}

/** Holat kodiga mos nishon uslubi (rang yagona indikator emas — matn ham bor). */
function statusBadgeClass(status: string): string {
  if (status === "closed") return "badge badge-brand";
  if (status === "new") return "badge";
  return "badge badge-info";
}

function trackError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isOffline) return "Server bilan aloqa yo'q — treking xizmati vaqtincha ishlamayapti.";
    if (error.status === 429) return "Urinishlar soni oshib ketdi. Birozdan so'ng qayta urinib ko'ring.";
    return error.message;
  }
  return "Kutilmagan xatolik yuz berdi.";
}

export function TrackingSection({
  initialId,
  hotline,
}: {
  /** Murojaat formasidan kelgan ID (kuzatish maydoni avtomatik to'ldiriladi). */
  initialId: string;
  hotline: string;
}) {
  const { t, locale } = useI18n();
  const appealTypes = useAppealTypes();
  const [method, setMethod] = useState<Method>("last4");
  const [ticket, setTicket] = useState(initialId);
  const [last4, setLast4] = useState("");
  const [otp, setOtp] = useState("");
  const [otpSent, setOtpSent] = useState("");
  const [otpBusy, setOtpBusy] = useState(false);
  const [track, setTrack] = useState<TrackState>({ phase: "idle" });
  const ticketInput = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!initialId) return;
    setTicket(initialId);
    ticketInput.current?.focus();
  }, [initialId]);

  const activeStep = track.data
    ? trackingSteps.findIndex((step) => step.status === track.data?.status)
    : -1;

  async function requestOtp() {
    const appealId = ticket.trim().toUpperCase();
    if (!appealId) {
      setTrack({ phase: "error", message: "Avval murojaat ID sini kiriting." });
      return;
    }
    setOtpBusy(true);
    try {
      const response = await appealsApi.trackOtp(appealId);
      setOtpSent(response.phone_masked);
      setTrack({ phase: "idle" });
    } catch (error) {
      setTrack({ phase: "error", message: trackError(error) });
    } finally {
      setOtpBusy(false);
    }
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const appealId = ticket.trim().toUpperCase();
    if (!appealId) {
      setTrack({ phase: "error", message: "Murojaat ID sini kiriting." });
      return;
    }
    if (method === "last4" && !/^\d{4}$/.test(last4.trim())) {
      setTrack({ phase: "error", message: "Telefon raqamingizning oxirgi 4 raqamini kiriting." });
      return;
    }
    if (method === "otp" && !/^\d{6}$/.test(otp.trim())) {
      setTrack({ phase: "error", message: "SMS orqali kelgan 6 xonali kodni kiriting." });
      return;
    }

    setTrack({ phase: "loading" });
    try {
      const data = await appealsApi.track(
        method === "last4"
          ? { appeal_id: appealId, phone_last4: last4.trim() }
          : { appeal_id: appealId, otp: otp.trim() },
      );
      setTrack({ phase: "done", data });
    } catch (error) {
      setTrack({ phase: "error", message: trackError(error) });
    }
  }

  const typeTitle =
    track.data?.type_label ||
    appealTypes.find((item) => item.code === track.data?.type)?.title ||
    track.data?.type;

  return (
    <section className="section section-alt" id="tracking" aria-labelledby="tracking-title">
      <div className="container">
        <SplitHead
          scene="tracking"
          side="right"
          eyebrow="Treking"
          title="Murojaatingiz qayerda?"
          titleId="tracking-title"
          lead="SMS'dagi ID va telefonning oxirgi 4 raqami (yoki SMS-kod) bilan tekshiring."
        />

        <div className="tracking" data-reveal>
          <div>
            <div className="segmented tracking-method" role="tablist" aria-label="Tasdiqlash usuli">
              <button
                type="button"
                role="tab"
                aria-selected={method === "last4"}
                aria-pressed={method === "last4"}
                onClick={() => setMethod("last4")}
              >
                Telefon (4 raqam)
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={method === "otp"}
                aria-pressed={method === "otp"}
                onClick={() => setMethod("otp")}
              >
                SMS-kod
              </button>
            </div>

            <form className="tracking-form" onSubmit={submit}>
              <div className="tracking-fields">
                <span className="field">
                  <label className="label" htmlFor="ticket">
                    Murojaat ID
                  </label>
                  <span className="input-group">
                    <span className="input-icon">
                      <Icon name="search" size={18} />
                    </span>
                    <input
                      id="ticket"
                      ref={ticketInput}
                      className="input num"
                      placeholder={APPEAL_ID_PATTERN}
                      value={ticket}
                      onChange={(event) => setTicket(event.target.value)}
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </span>
                </span>

                {method === "last4" ? (
                  <span className="field tracking-last4">
                    <label className="label" htmlFor="last4">
                      Telefon (oxirgi 4 raqam)
                    </label>
                    <span className="input-group">
                      <span className="input-icon">
                        <Icon name="phone" size={18} />
                      </span>
                      <input
                        id="last4"
                        className="input num"
                        placeholder="1234"
                        inputMode="numeric"
                        maxLength={4}
                        value={last4}
                        onChange={(event) =>
                          setLast4(event.target.value.replace(/\D/g, "").slice(0, 4))
                        }
                        autoComplete="off"
                      />
                    </span>
                  </span>
                ) : (
                  <span className="field tracking-last4">
                    <label className="label" htmlFor="otp">
                      SMS-kod (6 raqam)
                    </label>
                    <span className="input-group">
                      <span className="input-icon">
                        <Icon name="key" size={18} />
                      </span>
                      <input
                        id="otp"
                        className="input num"
                        placeholder="000000"
                        inputMode="numeric"
                        autoComplete="one-time-code"
                        maxLength={6}
                        value={otp}
                        onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 6))}
                      />
                    </span>
                  </span>
                )}
              </div>

              {method === "otp" ? (
                <button
                  type="button"
                  className="btn btn-outline"
                  onClick={() => void requestOtp()}
                  disabled={otpBusy}
                >
                  <Icon name="message" size={16} />
                  {otpBusy ? "Yuborilmoqda…" : otpSent ? "Kodni qayta yuborish" : "SMS-kod olish"}
                </button>
              ) : null}

              <button className="btn btn-primary" type="submit" disabled={track.phase === "loading"}>
                {track.phase === "loading" ? t("state.loading") : t("action.check")}
              </button>
            </form>

            {method === "otp" && otpSent ? (
              <p className="alert alert-info" role="status" style={{ marginTop: 14 }}>
                <Icon name="info" size={18} />
                <span>Kod {otpSent} raqamiga yuborildi. U 5 daqiqa amal qiladi.</span>
              </p>
            ) : null}

            {track.phase === "error" ? (
              <p className="alert alert-error" role="alert" style={{ marginTop: 14 }}>
                <Icon name="alert" size={18} />
                <span>{track.message}</span>
              </p>
            ) : (
              <p className="hint" style={{ marginTop: 12 }}>
                Masalan: {APPEAL_ID_SAMPLE} · Ishonch telefoni: {hotline}
              </p>
            )}

            {track.data ? (
              <div className="track-result" role="status">
                <div className="track-result-head">
                  <b className="num">{track.data.appeal_id}</b>
                  <span className={statusBadgeClass(track.data.status)}>
                    {appealStatusLabels[track.data.status] ?? track.data.status_label}
                  </span>
                </div>
                <dl className="track-result-meta">
                  <div>
                    <dt>Turi</dt>
                    <dd>{typeTitle}</dd>
                  </div>
                  <div>
                    <dt>Qabul qilingan</dt>
                    <dd>{formatDateTime(track.data.created_at, locale)}</dd>
                  </div>
                  {track.data.address ? (
                    <div>
                      <dt>Manzil</dt>
                      <dd>{track.data.address}</dd>
                    </div>
                  ) : null}
                  {track.data.executor ? (
                    <div>
                      <dt>Ijrochi</dt>
                      <dd>{track.data.executor}</dd>
                    </div>
                  ) : null}
                  {track.data.closed_at ? (
                    <div>
                      <dt>Yakunlangan</dt>
                      <dd>{formatDate(track.data.closed_at, locale)}</dd>
                    </div>
                  ) : null}
                  {track.data.result_tag ? (
                    <div>
                      <dt>Natija</dt>
                      <dd>{track.data.result_tag}</dd>
                    </div>
                  ) : null}
                </dl>
                {track.data.result_note ? <p>{track.data.result_note}</p> : null}
              </div>
            ) : null}
          </div>

          <div className="tracking-steps">
            <span className="tracking-steps-title">Ijro tarixi</span>
            {trackingSteps.map((step, index) => {
              const done = activeStep >= 0 && index < activeStep;
              const current = activeStep === index;
              const at = track.data?.history.find((item) => item.status === step.status)?.at;
              return (
                <div
                  className={`tracking-step${done ? " is-done" : ""}${current ? " is-current" : ""}`}
                  key={step.status}
                  style={{ "--step-index": index } as CSSProperties}
                >
                  <span className="step-dot" aria-hidden="true">
                    {done ? "✓" : index + 1}
                  </span>
                  <div>
                    <b>
                      {step.title}
                      {current ? <span className="visually-hidden"> (joriy bosqich)</span> : null}
                      {done ? <span className="visually-hidden"> (bajarildi)</span> : null}
                    </b>
                    {at ? <em>{formatDateTime(at, locale)}</em> : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
